import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WidgetCard from './WidgetCard';
import CardTitle from './CardTitle';
import CenteredSpinner from '../../../components/CenteredSpinner';
import DashboardServices from '../../../services/DashboardServices';
import localStorageService from '../../../services/localStorageService';

// Progress bar segment component
const SegmentBar = ({ acceptanceRate = 0, rejectionRate = 0 }) => {
  const theme = useTheme();
  const acceptWidth = Math.min(Math.max(acceptanceRate, 0), 100);
  const rejectWidth = Math.min(Math.max(rejectionRate, 0), 100 - acceptWidth);
  return (
    <Box sx={{ display: 'flex', width: '100%', height: 8, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ width: `${acceptWidth}%`, bgcolor: theme.palette.success.main }} />
      <Box sx={{ width: `${rejectWidth}%`, bgcolor: theme.palette.error.main }} />
    </Box>
  );
};

const AssignmentAcceptanceRate = ({ title = 'Assignment Acceptance Rate' }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    total_assignments: 0,
    accepted_count: 0,
    rejected_count: 0,
    acceptance_rate: 0,
    rejection_rate: 0,
  });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const storedUser = localStorageService?.getItem?.('user') ?? null;
        const roles = (storedUser?.roles) || (storedUser?.role) || storedUser?.rolesArray || [];
        const normalizedRoles = Array.isArray(roles) ? roles.map(r => String(r).toLowerCase()) : [];
        const targetRoles = ['organization admin', 'case officer', 'case manager', 'organization head', 'director cid', 'director ncpa', 'deputy director','oic','department head','team leader first respondent','call center officer mowca', 'counselling officer', 'legal officer'];
        const hasTargetRole = normalizedRoles.some(r => targetRoles.includes(r));

        const params = {};
        if (hasTargetRole) {
          const orgId = storedUser?.employee?.organization_id ?? storedUser?.organization_id ?? storedUser?.org?.id ?? null;
          if (orgId) params.organization_id = orgId;
        }
        const targetEmployee_id_Roles = ['case officer', 'first respondent', 'counselling officer', 'legal officer'];
        const needToPassUserId = normalizedRoles.some(r => targetEmployee_id_Roles.includes(r));
        if (needToPassUserId) {
          const employeeId = storedUser?.employee?.id ?? storedUser?.employee_id ?? null;
          if (employeeId) params.assigned_to_officer_id = employeeId;
        }


        const res = await DashboardServices.getDashboardAssignmentAcceptanceRate(params);
        const payload = res?.data ?? res;
        const d = payload?.view?.data ?? payload?.data ?? {};

        if (mounted) {
          setData({
            total_assignments: Number(d.total_assignments) || 0,
            accepted_count: Number(d.accepted_count) || 0,
            rejected_count: Number(d.rejected_count) || 0,
            acceptance_rate: Number(d.acceptance_rate) || 0,
            rejection_rate: Number(d.rejection_rate) || 0,
          });
        }
      } catch (err) {
        console.error('Failed to load assignment acceptance rate', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, []);

  //if (loading) return <WidgetCard sx={{ minHeight: 200 ,height:'100%'}}><CenteredSpinner /></WidgetCard>;

  const {
    total_assignments,
    accepted_count,
    rejected_count,
    acceptance_rate,
    rejection_rate,
  } = data;

  return (
    <WidgetCard sx={{ minHeight: 200, height: '100%' }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <CardTitle>{title}</CardTitle>
          <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, px: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#333333' }}>{acceptance_rate}%</Typography>
            <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: '#333333' }}>Accepted</Typography>
          </Box>
        </Stack>

        <Typography className="mt--2" variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
          {accepted_count.toLocaleString()} / {total_assignments.toLocaleString()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Accepted / Total Assignments
        </Typography>

        <SegmentBar acceptanceRate={acceptance_rate} rejectionRate={rejection_rate} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: theme.palette.success.main }} />
            <Typography variant="caption" color="text.secondary">Accepted: {accepted_count}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: theme.palette.error.main }} />
            <Typography variant="caption" color="text.secondary">Rejected: {rejected_count}</Typography>
          </Stack>
        </Box>
      </Stack>
    </WidgetCard>
  );
};

export default AssignmentAcceptanceRate;
